import * as React from "react"

import { cn } from "@/lib/utils"
import { TONE_TEXT_CLASS } from "@/lib/status"
import { ProgressBar, ProgressRing } from "@/components/ui/progress-bar"

/*
  KeyFacts — the compact facts strip of a page head (project header 04 #1): cells of label / value /
  meta separated by thin vertical lines, optionally with a progress bar or a ring. Wraps to 3 columns
  on tablets and 2 on phones (the dividers follow the wrap — no stray line at a row start).

    <KeyFacts
      columns="1.3fr 1.3fr 1fr 1fr 1fr 1.2fr"
      items={[
        { key: 'contract', label: 'Contract value', prefix: 'AED', value: '498,855', meta: '437,850 base + 61,005 VOs' },
        { key: 'invoiced', label: 'Invoiced', prefix: 'AED', value: '200,282', suffix: '40%',
          progress: 40, progressTone: 'lime', meta: 'of contract · all received' },
        { key: 'stages', label: 'Stages', ring: { value: 22 }, value: '2 of 9 approved', meta: 'Next: Material Submittal' },
        { key: 'exec', label: 'Execution', ring: { value: 92, tone: 'lime' }, value: '11 of 12 done', meta: '2 not required' },
        { key: 'handover', label: 'Handover', value: '13 Aug 2026', size: 'md',
          meta: '48 days overdue', metaTone: 'red' },
        { key: 'team', label: 'Team', content: <TeamFacts … /> },
      ]} />

  Item: { key, label, value, prefix (small, before), suffix (small, after), size ('lg' Chivo 20 |
  'md' Chivo 16 | 'sm' 13.5 bold — default 'lg', 'sm' next to a ring), valueTone, meta (node),
  metaTone, progress (0–100) + progressTone + progressLabel, ring ({ value, tone, centerLabel, label }),
  content (replaces the value line), title (tooltip), hidden, testId }.
  Props: items, columns (grid template from `lg`, default equal columns), tabletColumns (3),
  phoneColumns (2), loading, className, testId.
*/

const VALUE_SIZE = {
  lg: "font-heading text-[20px] font-extrabold leading-[1.15] tracking-[-0.02em]",
  md: "font-heading text-[16px] font-extrabold leading-[1.2] tracking-[-0.01em]",
  sm: "text-[13.5px] font-bold leading-[1.3]",
}

function KeyFactCell({ item, loading }) {
  const size = item.size || (item.ring ? "sm" : "lg")
  const valueLine = item.content ?? (
    <span className={cn("inline-flex min-w-0 max-w-full flex-wrap items-baseline gap-x-1 text-ink", VALUE_SIZE[size] || VALUE_SIZE.lg, item.valueTone && TONE_TEXT_CLASS[item.valueTone])}>
      {item.prefix ? <span className="font-sans text-[12px] font-bold tracking-normal text-ink-3">{item.prefix}</span> : null}
      <span className="min-w-0 break-words">{item.value ?? <span className="text-ink-4">—</span>}</span>
      {item.suffix ? <span className="font-sans text-[13px] font-semibold tracking-normal text-ink-2">{item.suffix}</span> : null}
    </span>
  )
  return (
    <div
      className="flex min-w-0 flex-col gap-1 border-l border-line px-[18px]"
      title={item.title}
      data-testid={item.testId}
    >
      <dt className="truncate text-[11.5px] font-semibold text-ink-3">{item.label}</dt>
      {loading ? (
        <dd className="ui-skeleton-shimmer h-6 w-24 rounded-md" role="status"><span className="sr-only">Loading</span></dd>
      ) : (
        <dd className="flex min-h-[28px] min-w-0 items-center gap-2.5">
          {item.ring ? (
            <ProgressRing
              value={item.ring.value}
              tone={item.ring.tone || "navy"}
              centerLabel={item.ring.centerLabel}
              size={item.ring.size || 40}
              label={item.ring.label || (typeof item.label === "string" ? item.label : undefined)}
            />
          ) : null}
          <div className="min-w-0">{valueLine}</div>
        </dd>
      )}
      {!loading && item.progress !== undefined && item.progress !== null ? (
        <dd><ProgressBar
          value={item.progress}
          tone={item.progressTone || "navy"}
          label={item.progressLabel || (typeof item.label === "string" ? item.label : undefined)}
          className="my-0.5"
        /></dd>
      ) : null}
      {item.meta ? (
        <dd className={cn("min-w-0 text-[12px] leading-[1.35] text-ink-3", item.metaTone && cn(TONE_TEXT_CLASS[item.metaTone], "font-bold"))}>
          {item.meta}
        </dd>
      ) : null}
    </div>
  )
}

function KeyFacts({ items = [], columns, tabletColumns = 3, phoneColumns = 2, loading = false, className, testId = "key-facts" }) {
  const visible = items.filter((item) => item && !item.hidden)
  const style = {
    "--kf-cols": columns || `repeat(${Math.max(1, visible.length)}, minmax(0, 1fr))`,
    "--kf-cols-md": `repeat(${tabletColumns}, minmax(0, 1fr))`,
    "--kf-cols-sm": `repeat(${phoneColumns}, minmax(0, 1fr))`,
  }
  return (
    // The grid starts 19px left of the box (18px padding + 1px line), so the line and padding of every
    // cell that starts a row are clipped away, however the cells wrap.
    <div className={cn("min-w-0 overflow-hidden", className)} data-testid={testId}>
      <dl
        className="-ml-[19px] grid gap-y-4 [grid-template-columns:var(--kf-cols-sm)] md:[grid-template-columns:var(--kf-cols-md)] lg:[grid-template-columns:var(--kf-cols)]"
        style={style}
      >
        {visible.map((item, index) => (
          <KeyFactCell key={item.key || index} item={item} loading={loading} />
        ))}
      </dl>
    </div>
  )
}

export { KeyFacts }
